import { useState } from 'react';
import { Button, Link, TextField } from '@mui/material';
import { DEAL_PERMITTED_STAGES } from 'common/constants/analyst-dashboards';
import { ConditionalLoader } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { Container, DealNameContainer, Field, Label, Row, StyledAutocomplete, StyledBadge, StyledLinkSpan, Value } from './ValuationDealInfo.styles';
import { Section } from '../Section';
import { DealStageState, ValuationDealInfoProps } from './ValuationDealInfo.props';
import { UserSection } from '../ValuedInformation/ValuedInformation.styles';

const ValuationDealInfo = ({ deal, isLoading, onDealStageChange, onSendFinalInvoice }: ValuationDealInfoProps) => {
  const [dealStageState, setDealStageState] = useState<DealStageState>(DealStageState.IDLE);
  const handleDealStageClick = () => {
    setDealStageState(DealStageState.EDITING);
  }
  
  const handleDealStageChange = async (value: any) => {
    setDealStageState(DealStageState.UPDATING);
    try {
      await onDealStageChange(value.id);
    } finally {
      setDealStageState(DealStageState.IDLE);
    }
  }

  const allDealStageOptions = deal?.dealStageOptions?.sort((a: any, b: any) => DEAL_PERMITTED_STAGES.indexOf(b.title) - DEAL_PERMITTED_STAGES.indexOf(a.title)).map((option: any) => ({
    id: option.id.status_id,
    label: option.title,
  }));

  const renderEngagementLetterLink = () => {
    if (deal?.signedPDF) {
      return (
        <Link href={deal.signedPDF} target="_blank" rel="noopener noreferrer">
          {' Signed EL'}
        </Link>
      );
    }
    if (deal?.elURL) {
      return (
        <Link href={deal.elURL} target="_blank" rel="noopener noreferrer">
          {' EL'}
        </Link>
      );
    }
    return null;
  };

  return (
    <Section title="Deal">
      <Container>
        {/* Header with Deal Name */}
        <DealNameContainer>
          <ConditionalLoader isLoading={isLoading && !deal?.dealName} width={150}>
            <Link href={deal?.dealUrl} target="_blank" rel="noopener noreferrer">
              <StyledLinkSpan>{deal?.dealName || '-'}</StyledLinkSpan>
            </Link>
          </ConditionalLoader>
          {deal?.dealNotice && (
            <span>NOTICE: {deal.dealNotice}</span>
          )}
        </DealNameContainer>
        {/* Deal Stage in separate row */}
        <Row>
          <ConditionalLoader isLoading={dealStageState === DealStageState.UPDATING} width={150}>
            {dealStageState === DealStageState.IDLE && (
              <StyledBadge onClick={handleDealStageClick}>
                {deal?.dealStage || 'Deal Stage not set'}
              </StyledBadge>
            )}
            {dealStageState === DealStageState.EDITING && (
              <StyledAutocomplete
                value={allDealStageOptions?.find((option: any) => option.label === deal?.dealStage) || null}
                options={allDealStageOptions || []}
                getOptionDisabled={(option: any) => !DEAL_PERMITTED_STAGES.includes(option.label)}
                renderInput={params => <TextField {...params} variant="outlined" />}
                disableClearable
                onChange={(event, value) => {
                  handleDealStageChange(value);
                }}
                renderOption={(props, option: any) => (
                  <li {...props} key={option.id} style={{
                    fontSize: '0.875rem',
                  }}>
                    {option.label}
                  </li>
                )}
              />
            )}
            <Button variant="contained" color="primary" size="small" sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }} onClick={onSendFinalInvoice} disabled={dealStageState === DealStageState.UPDATING || isLoading}>Send Final Invoice</Button>
          </ConditionalLoader>
        </Row>

        {/* Deal Value and Deal Date */}
        <Row>
          <Field>
            <Label>Deal Value:</Label>
            <ConditionalLoader isLoading={isLoading && !deal?.dealTotal} width={150}>
              <Value>{deal?.dealTotal || '-'}</Value>
            </ConditionalLoader>
          </Field>
          <Field>
            <Label>Deal Date</Label>
            <ConditionalLoader isLoading={isLoading && !deal?.dealDate} width={150}>
              <Value>{deal?.dealDate || '-'}</Value>
            </ConditionalLoader>
          </Field>
        </Row>

        {/* Due and Net */}
        <Row>
          <Field>
            <Label>Due:</Label>
            <ConditionalLoader isLoading={isLoading && !deal?.due} width={150}>
              <Value>{deal?.due || '-'}</Value>
            </ConditionalLoader>
          </Field>
          <Field>
            <Label>NET:</Label>
            <ConditionalLoader isLoading={isLoading && deal?.net === undefined} width={150}>
              <Value>{formatCurrency({ value: deal?.net || '-' })}</Value>
            </ConditionalLoader>
          </Field>
        </Row>

        {/* Contract Information */}
        <Row>
          <ConditionalLoader isLoading={isLoading && deal?.contractStatus === undefined} width={150}>
            <Value>Contract: {deal?.contractStatus || '-'}</Value>
            {renderEngagementLetterLink()}
          </ConditionalLoader>
        </Row>

        {/* Engagement Manager */}
        <Field>
          <Label>Engagement Manager</Label>
          <UserSection>
            <Avatar
              src={deal?.engagementManagerImageSrc || ''}
              name={deal?.engagementManager || '-'}
              roleTitle={deal?.engagementManagerRole || '-'}
              isLoading={isLoading && !deal?.engagementManager}
            />
          </UserSection>
        </Field>
      </Container>
    </Section>
  )
};

export default ValuationDealInfo;
